import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

import { usePlayerEvent } from '../utils/usePlayerEvent';
import { useAppDispatch, useAppSelector } from '../../modules/hooks';
import {
  upsertAssetWatchtime,
  saveAssetWatchtime,
} from '../../modules/watchtime/actions';
import {
  selectWatchtimeSavedAt,
  selectWatchtimeUpdatedAt,
} from '../../modules/watchtime';
import { isLoggedIn } from '../../services/schibstedAccount';
import { getCategoryMetadata } from '../../shared/helpers/categoryHelpers';
import { getUnixTimestamp } from '../../utils/datetime';
import { config } from '../../config';
import { unleash } from '../../services/unleash';
import { useVendor } from '../VendorPovider/VendorPovider';
import { usePlayer } from '../../player/hooks/usePlayer';

import type { SeekParam } from '../utils/usePlayerEvent';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

const getSkipTime = (
  duration: number,
  percentage: number,
  max: number
): number => {
  return Math.min(Math.ceil(duration * (percentage / 100)), max);
};

const shouldSaveAssetWatchtime = (
  updatedAt?: number,
  savedAt?: number
): boolean => {
  if (!unleash.isEnabled('watchtime')) {
    return false;
  }
  if (!updatedAt) {
    return false;
  }
  if (!savedAt) {
    return true;
  }
  // save continue watching only if at least X seconds passed from previous update
  return dayjs(savedAt)
    .add(config.watchtime.saveInterval, 'second')
    .isBefore(Date.now());
};

export const isValidAssetType = (
  assetType: SvpAssetAdditional['assetType']
) => {
  return assetType === 'video';
};

export const isValidStreamType = (
  streamType: SvpAssetAdditional['streamType']
) => {
  return streamType === 'vod' || streamType === 'wasLive';
};

export const isValidCategory = (category: {
  additional: Pick<SvpCategoryAdditional['additional'], 'metadata'>;
}) => {
  return getCategoryMetadata(category).isWatchtimeEnabled?.value === 'true';
};

const isValidAsset = (asset: SvpAssetAdditional): boolean => {
  return (
    isValidAssetType(asset.assetType) &&
    isValidStreamType(asset.streamType) &&
    isValidCategory(asset.category)
  );
};

const isWatchtimeSupported = (asset: SvpAssetAdditional): boolean => {
  return unleash.isEnabled('watchtime') && isValidAsset(asset);
};

const isVideoWatchedContinuously = (
  time: number,
  seekOffset: number
): boolean => {
  return Math.abs(time - seekOffset) > config.watchtime.minSeekDiff;
};

const useAssetWatchtime = (asset: SvpAssetAdditional) => {
  const { vendor } = useVendor();
  const player = usePlayer();
  const dispatch = useAppDispatch();

  const updatedAt = useAppSelector(selectWatchtimeUpdatedAt);
  const savedAt = useAppSelector(selectWatchtimeSavedAt);

  const updatedAtRef = useRef(updatedAt);

  useEffect(() => {
    updatedAtRef.current = updatedAt;
  }, [updatedAt]);

  useEffect(() => {
    const listener = () => {
      if (updatedAtRef.current && isWatchtimeSupported(asset)) {
        dispatch(
          saveAssetWatchtime({
            assetId: asset.id,
            vendor: asset.provider,
          })
        );
      }
    };

    window.addEventListener('pagehide', listener);

    return () => {
      listener();

      window.removeEventListener('pagehide', listener);
    };
  }, [dispatch, asset]);

  useEffect(() => {
    if (shouldSaveAssetWatchtime(updatedAt, savedAt)) {
      dispatch(
        saveAssetWatchtime({
          assetId: asset.id,
          vendor: asset.provider,
        })
      );
    }
  }, [dispatch, updatedAt, savedAt, asset.id, asset.provider]);

  const seekOffset = useRef<number>(0);

  const seekListener = useCallback(({ offset }: SeekParam) => {
    seekOffset.current = offset;
  }, []);

  const timeListener = useCallback(
    async (time: number, duration: number) => {
      const jw = player?.getJWPlayer();
      const playlistItem = jw?.getPlaylistItem(0);

      // make sure that the new playlist item is loaded
      if (!isWatchtimeSupported(asset) || playlistItem?.id !== asset.id) {
        return;
      }

      const loggedIn = await isLoggedIn();

      if (!loggedIn) {
        return;
      }

      const startSkipTime = getSkipTime(
        duration,
        config.watchtime.skipStartPercentage,
        config.watchtime.maxSkipWindow
      );

      if (time < startSkipTime) {
        return;
      }

      // save time only if video has been watched continuously for more than X seconds
      if (!isVideoWatchedContinuously(time, seekOffset.current)) {
        return;
      }

      dispatch(
        upsertAssetWatchtime({
          vendor,
          data: {
            assetId: asset.id,
            time: Math.floor(time),
            updatedAt: Math.floor(getUnixTimestamp(Date.now())),
          },
        })
      );
    },
    [dispatch, asset, vendor, player]
  );

  usePlayerEvent('seek', seekListener);
  usePlayerEvent('time', timeListener);
};

export default useAssetWatchtime;
